import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Building2, DollarSign, Users, ShoppingCart, FileText, Loader2 } from 'lucide-react';
import { NewsChallengesWidget } from '../../components/dashboard/NewsChallengesWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GroupAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/admin/analytics/group`, { credentials: 'include' });
        if (r.ok) setData(await r.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  if (!data) return <p className="text-red-600">Erreur de chargement</p>;

  const chartData = (data.per_agency || []).map((a) => ({
    name: a.name,
    'CA total': a.total_revenue,
    'CA du mois': a.monthly_revenue,
  }));

  return (
    <div className="space-y-6" data-testid="group-analytics">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="group-analytics-title">
            Analytics Groupe LNC
          </h1>
          <p className="text-slate-500 mt-1">Vue consolidee de toutes les filiales</p>
        </div>
        <a
          href={`${API}/admin/analytics/group/export`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
          data-testid="export-csv-btn"
        >
          Exporter en CSV
        </a>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Filiales actives</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="stat-active-agencies">{data.totals.active_agencies}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="p-2 rounded-lg bg-emerald-100">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500">CA total Groupe</p>
            <p className="text-xl font-bold text-emerald-600" data-testid="stat-total-revenue">
              {(data.totals.total_revenue || 0).toLocaleString('fr-FR')} €
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="p-2 rounded-lg bg-amber-100">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500">CA du mois</p>
            <p className="text-xl font-bold text-amber-600" data-testid="stat-monthly-revenue">
              {(data.totals.monthly_revenue || 0).toLocaleString('fr-FR')} €
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="p-2 rounded-lg bg-purple-100">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Utilisateurs</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="stat-total-users">{data.totals.total_users}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="p-2 rounded-lg bg-rose-100">
                <ShoppingCart className="w-5 h-5 text-rose-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Ventes total</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="stat-total-sales">{data.totals.total_sales}</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart CA par filiale */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Chiffre d&apos;affaires par filiale</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} €`} />
              <Legend />
              <Bar dataKey="CA total" fill="#4f46e5" radius={[6, 6, 0, 0]} />
              <Bar dataKey="CA du mois" fill="#10b981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Table détails */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Detail par filiale</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Filiale</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Ventes</TableHead>
                <TableHead>FDC</TableHead>
                <TableHead>CA du mois</TableHead>
                <TableHead>CA total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.per_agency.map((a) => (
                <TableRow key={a.agency_id} data-testid={`agency-row-${a.slug}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{a.name}</p>
                      <p className="text-xs text-slate-400">{a.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {a.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    ) : (
                      <Badge className="bg-slate-200">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {a.users}
                      <p className="text-xs text-slate-400">{a.commercials} AC · {a.rh} RH</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {a.total_sales}
                    <p className="text-xs text-slate-400">{a.monthly_sales} ce mois</p>
                  </TableCell>
                  <TableCell><FileText className="w-3 h-3 inline text-slate-400 mr-1" />{a.fdc}</TableCell>
                  <TableCell className="font-medium text-amber-600">{(a.monthly_revenue || 0).toLocaleString('fr-FR')} €</TableCell>
                  <TableCell className="font-bold text-emerald-600">{(a.total_revenue || 0).toLocaleString('fr-FR')} €</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Actualités et Challenges */}
      <NewsChallengesWidget />
    </div>
  );
};

export default GroupAnalytics;
